import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  Users, MagnifyingGlass, EnvelopeSimple, X, ArrowLeft, PaperPlaneTilt,
  ShieldCheck, Globe, Warning, Gift,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({ users: [], total: 0, country_counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (country) params.country = country;
      const { data: payload } = await api.get("/super-admin/users", { params });
      setData(payload);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmitSearch = (e) => { e.preventDefault(); refresh(); };

  const filtered = data.users || [];

  const toggleOne = (email) => {
    setSelected((prev) => {
      const isOn = prev.has(email);
      const arr = Array.from(prev);
      return new Set(isOn ? arr.filter((e) => e !== email) : [...arr, email]);
    });
  };
  const allSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.email));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((u) => u.email)));
  };

  if (!authLoading && !user?.is_super_admin) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-xl mx-auto px-4 py-16 text-center" data-testid="users-gated">
          <h1 className="font-serif-display text-3xl">Super Admin only</h1>
          <p className="text-muted mt-3">This database view is restricted.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="users-page">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-amber-700 text-[10px] uppercase tracking-[0.25em] font-semibold">
          <ShieldCheck size={14} weight="duotone" /> Super Admin · User Database
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          All Signed-up <span className="italic text-gold">Users</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-3 max-w-3xl font-light">
          Every customer who ever signed in via Google. Auto-refreshed on each new
          sign-up. Filter, select, and broadcast a custom email to any subset in one click.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/grant-unlock")}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-50 text-amber-900 px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 transition"
            data-testid="open-grant-unlock-btn"
          >
            <Gift size={13} weight="fill" /> Grant a free report
          </button>
        </div>

        {/* Controls */}
        <form onSubmit={onSubmitSearch} className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3" data-testid="users-filter-form">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-soft bg-white text-sm"
              data-testid="users-search"
            />
          </div>
          <select
            value={country} onChange={(e) => setCountry(e.target.value)}
            className="px-3 py-2.5 rounded-md border border-soft bg-white text-sm"
            data-testid="users-country-filter"
          >
            <option value="">All countries</option>
            {Object.entries(data.country_counts || {}).map(([c, n]) => (
              <option key={c} value={c}>{c} ({n})</option>
            ))}
          </select>
          <button type="submit" className="btn-primary rounded-md px-5 py-2.5 text-sm" data-testid="users-apply-filters">
            Apply
          </button>
        </form>

        {/* Stats row */}
        <div className="mt-5 flex items-center flex-wrap gap-3 text-sm text-[#3F2D1B]" data-testid="users-stats">
          <span className="px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 font-medium">
            <Users size={13} weight="duotone" className="inline mr-1" /> {data.total} total
          </span>
          {typeof data.returned === "number" && data.total > data.returned ? (
            <span
              className="px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800 text-xs"
              title="Refine the search filters to load fewer rows at a time."
              data-testid="users-truncated-warning"
            >
              Showing {data.returned.toLocaleString()} most-recent of {data.total.toLocaleString()}
            </span>
          ) : null}
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
            {selected.size} selected
          </span>
          {Object.entries(data.country_counts || {}).slice(0, 6).map(([c, n]) => (
            <span key={c} className="px-3 py-1.5 rounded-full bg-white border border-soft text-xs">
              <Globe size={12} weight="duotone" className="inline mr-1" /> {c}: {n}
            </span>
          ))}
        </div>

        {/* Send button */}
        <div className="mt-5 flex items-center gap-3">
          <button
            disabled={selected.size === 0}
            onClick={() => setComposeOpen(true)}
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50"
            data-testid="users-compose-broadcast"
          >
            <EnvelopeSimple size={14} weight="duotone" className="inline mr-1.5" />
            Email {selected.size} selected
          </button>
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="text-xs text-muted hover:text-[#3F2D1B]">
              Clear selection
            </button>
          )}
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto rounded-2xl border border-soft bg-white" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-amber-50 text-[#3F2D1B]">
              <tr>
                <th className="px-3 py-2.5 text-left w-10">
                  <input
                    type="checkbox" checked={allSelected} onChange={toggleAll}
                    data-testid="users-select-all"
                  />
                </th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-left">Country</th>
                <th className="px-3 py-2.5 text-right">Wallet</th>
                <th className="px-3 py-2.5 text-left whitespace-nowrap">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted">No users found.</td></tr>
              ) : filtered.map((u) => (
                <tr key={u.user_id} className="border-t border-soft hover:bg-amber-50/40" data-testid={`users-row-${u.user_id}`}>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="checkbox"
                      checked={selected.has(u.email)}
                      onChange={() => toggleOne(u.email)}
                      data-testid={`users-select-${u.email}`}
                    />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-2 min-w-0">
                      {u.picture && (
                        <img src={u.picture} alt={u.name} className="w-7 h-7 rounded-full object-cover" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-[#3F2D1B] truncate">{u.name || "—"}</div>
                        {u.is_super_admin && (
                          <span className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Super admin</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-[#3F2D1B] break-all">{u.email}</td>
                  <td className="px-3 py-2 align-top">
                    {u.country ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-900">{u.country}</span>
                    ) : <span className="text-xs text-muted">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top text-right tabular-nums">₹{(u.wallet_balance || 0).toFixed(0)}</td>
                  <td className="px-3 py-2 align-top text-xs text-muted whitespace-nowrap">
                    {(u.created_at || "").slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {composeOpen && (
        <ComposeBroadcastModal
          recipients={Array.from(selected)}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); toast.success(`Queued ${selected.size} emails`); setSelected(new Set()); }}
        />
      )}

      <Footer />
    </div>
  );
}

function ComposeBroadcastModal({ recipients, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    setBusy(true);
    try {
      const html = body
        .split(/\n\n+/)
        .map((p) => `<p style="margin:0 0 16px 0; line-height:1.6; color:#3F2D1B;">${
          p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br />")
        }</p>`)
        .join("");
      await api.post("/super-admin/users/broadcast", {
        recipient_emails: recipients,
        subject,
        html_body: html,
      });
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="users-compose-modal">
      <div className="bg-[#FAF4E8] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 sm:p-7">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-display text-2xl">Broadcast email</h2>
            <button onClick={onClose} className="text-muted hover:text-[#3F2D1B]" data-testid="users-compose-close">
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="text-xs text-muted mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            <Warning size={14} weight="duotone" className="text-amber-700 mt-0.5 shrink-0" />
            <span>
              About to send to <b>{recipients.length}</b> recipients via Resend, from
              your AstroKuberChat sender address. There is no &quot;undo&quot; once sent.
            </span>
          </div>
          <label className="block">
            <span className="label-eyebrow text-xs">Subject</span>
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-md border border-soft bg-white text-sm"
              placeholder="🌟 A new tool just dropped on AstroKuberChat"
              data-testid="users-compose-subject"
            />
          </label>
          <label className="block mt-4">
            <span className="label-eyebrow text-xs">Message body (plain text — paragraphs auto-wrap)</span>
            <textarea
              value={body} onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="mt-1 w-full px-3 py-2.5 rounded-md border border-soft bg-white text-sm font-mono"
              placeholder="Hi there,&#10;&#10;We just shipped …&#10;&#10;— Team AstroKuberChat"
              data-testid="users-compose-body"
            />
          </label>
          <div className="mt-5 flex items-center justify-end gap-3">
            <button onClick={onClose} className="btn-ghost rounded-full px-5 py-2.5 text-sm" data-testid="users-compose-cancel">Cancel</button>
            <button
              onClick={send} disabled={busy}
              className="btn-primary rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
              data-testid="users-compose-send"
            >
              <PaperPlaneTilt size={14} weight="duotone" className="inline mr-1.5" />
              {busy ? "Queuing…" : `Send to ${recipients.length} recipients`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
